import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, Select, Space, Typography, message } from 'antd';
import { putVisitNC } from '../../services/api';
import { getApiError } from '../../utils/apiError';
import { NcSetup, Question } from './checklistTypes';

const { TextArea } = Input;

const LEVELS = [
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'MAJOR', label: 'Major' },
  { value: 'MINOR', label: 'Minor' },
  { value: 'CONCERN', label: 'Concern' },
];

interface Props {
  visitId: string;
  question: Question | null;
  setup: NcSetup | null;
  contractors: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}

const NCDrawer: React.FC<Props> = ({ visitId, question, setup, contractors, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [showAll, setShowAll] = useState(false);
  const level: string | undefined = Form.useWatch('level', form);
  const categories = setup?.categories || [];
  const offered = question
    ? question.category_codes.map((code) => categories.find((c) => c.code === code)).filter((c) => !!c)
    : [];
  const chosenId: string | undefined = Form.useWatch('category_id', form);
  const options = (showAll || offered.length === 0 || (chosenId && !offered.some((c) => c && c.id === chosenId))
    ? categories : (offered as NcSetup['categories']));
  const guide = setup?.levels.find((l) => l.level === level);
  const recommended = question?.recommended_level || '';
  const deviates = !!recommended && !!level && level !== recommended;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!question) return;
    setError('');
    setShowAll(false);
    form.resetFields();
    if (!question.nc) {
      const first = categories.find((c) => c.code === question.category_codes[0]);
      form.setFieldsValue({
        level: question.recommended_level || undefined,
        category_id: first ? first.id : undefined,
        rectification_required: '',
      });
    }
    if (question.nc) {
      form.setFieldsValue({
        level: question.nc.level,
        category_id: question.nc.category_id,
        description: question.nc.description,
        rectification_required: question.nc.rectification_required,
        contractor_id: question.nc.contractor_id || undefined,
        location: question.nc.location || '',
        level_reason: question.nc.level_reason || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, form]);

  const save = async () => {
    if (!question) return;
    const values = await form.validateFields();
    setSaving(true);
    setError('');
    try {
      await putVisitNC(visitId, question.id, { ...values, contractor_id: values.contractor_id || null });
      message.success('NC saved.');
      onSaved();
    } catch (err) {
      setError(getApiError(err, 'Could not save the NC.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={question ? `NC for ${question.code}` : ''}
      open={!!question}
      onClose={onClose}
      size={480}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Close</Button>
          <Button type="primary" onClick={save} loading={saving}>Save NC</Button>
        </Space>
      }
    >
      {question && (
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }} message={question.text}
          description={(question.reference || question.raising_condition) ? (
            <>
              {question.reference && <div><Typography.Text strong>Reference: </Typography.Text>{question.reference}</div>}
              {question.raising_condition && (
                <div><Typography.Text strong>Raise the level if: </Typography.Text>{question.raising_condition}</div>
              )}
            </>
          ) : undefined}
        />
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical">
        <Form.Item name="level" label="NC level" rules={[{ required: true, message: 'Choose the level' }]}>
          <Select options={LEVELS.map((l) => ({ ...l, label: l.value === recommended ? `${l.label} (recommended)` : l.label }))} />
        </Form.Item>
        {guide && (
          <Typography.Paragraph type="secondary" style={{ marginTop: -12 }}>
            {guide.meaning}
            {guide.due_days != null && ` Rectification due in ${guide.due_days} days (reminder every ${guide.reminder_gap_days} days).`}
          </Typography.Paragraph>
        )}
        {deviates && (
          <Form.Item
            name="level_reason" label={`Why not ${recommended.charAt(0) + recommended.slice(1).toLowerCase()}?`}
            rules={[{ required: true, message: 'Give the reason for choosing a different level' }]}
            extra="The reviewer will see this."
          >
            <TextArea rows={2} />
          </Form.Item>
        )}
        <Form.Item name="category_id" label="Category" rules={[{ required: true, message: 'Choose the category' }]}>
          <Select showSearch optionFilterProp="label" options={options.map((c) => ({ value: c.id, label: `${c.code} - ${c.name}` }))} />
        </Form.Item>
        {!showAll && offered.length > 0 && offered.length < categories.length && (
          <Button type="link" size="small" style={{ marginTop: -16, marginBottom: 8, padding: 0 }} onClick={() => setShowAll(true)}>
            Show all categories
          </Button>
        )}
        <Form.Item
          name="location" label="Location"
          rules={[{ required: true, message: 'Say where it is (tower, floor, grid, member...)' }]}
          extra="For example: Tower B, 7th floor, grid C-4, column C4"
        >
          <Input maxLength={300} />
        </Form.Item>
        <Form.Item name="description" label="What was found" rules={[{ required: true, message: 'Describe the finding' }]}>
          <TextArea rows={4} />
        </Form.Item>
        <Form.Item
          name="rectification_required" label="Rectification required"
          rules={[{ required: true, message: 'State what must be done' }]}
        >
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item name="contractor_id" label="Contractor (optional)">
          <Select
            allowClear showSearch optionFilterProp="label"
            options={contractors.map((c) => ({ value: c.id, label: c.name }))}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default NCDrawer;
